import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { runnerApi, errorText, type LintIssue, type LintResult, type SchemaHealth } from '../../../api/migrationRunner';
import { Btn, Drawer, ErrorBox, Panel, Pill, Spinner, shortName, when } from './ui';
import DriftFixSheet from './DriftFixSheet';
import { Lint } from './PreviewPanel';
import type { RunnerNav } from './nav';

const RULE_TEXT: Record<string, (n: number) => string> = {
  'loose-root-file': (n) => `${n} loose file${n === 1 ? '' : 's'} outside dated folders (never run).`,
  'missing-scope': (n) => `${n} file${n === 1 ? ' has' : 's have'} no @scope header (treated as tenant).`,
  'copy-file': (n) => `${n} duplicate "copy" file${n === 1 ? '' : 's'}.`,
  'data-dump': (n) => `${n} data dump${n === 1 ? '' : 's'} registered as a runnable migration. Archive or move ${n === 1 ? 'it' : 'them'}.`,
  delimiter: (n) => `${n} file${n === 1 ? ' uses' : 's use'} DELIMITER (supported, but plain ; with BEGIN...END is simpler).`,
  'non-idempotent': (n) => `${n} statement${n === 1 ? '' : 's'} can't run twice (no IF NOT EXISTS).`,
  'bad-folder-name': (n) => `${n} folder${n === 1 ? '' : 's'} not named migrations-YYYY-M-D-<feature> (ignored).`,
  'no-number-prefix': (n) => `${n} file${n === 1 ? ' has' : 's have'} no number prefix (run order is alphabetical).`,
  'non-sql-file': (n) => `${n} non-.sql file${n === 1 ? '' : 's'} in migration folders (ignored).`,
  'unknown-requires': (n) => `${n} file${n === 1 ? ' needs' : 's need'} a folder (-- @requires) that isn't in migrations/, so ${n === 1 ? 'it' : 'they'} never run.`,
  'requires-later-folder': (n) => `${n} file${n === 1 ? ' needs' : 's need'} a folder that runs after ${n === 1 ? 'it' : 'them'} (skipped until a second run).`,
  'same-day-same-table': (n) => `${n} pair${n === 1 ? '' : 's'} of folders with the same date change the same table; they run in name order.`,
};

/**
 * Schema health by company (each company vs the template, with Fix drift).
 * Lives under Clean up › Template ("By company") since the Clean up tab.
 */
export const CompanyHealthPanel: React.FC<{ nav: RunnerNav; isOwner?: boolean; refreshKey?: number }> = ({ nav, isOwner = false, refreshKey = 0 }) => {
  const [fixFor, setFixFor] = useState<string | null>(null);
  const [health, setHealth] = useState<SchemaHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInSync, setShowInSync] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setHealth(await runnerApi.schemaHealth());
    } catch (err) {
      setError(errorText(err, 'Schema check failed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const companies = (health?.companies ?? []).filter((c) => showInSync || c.state !== 'in_sync');

  return (
    <>
      {error && <ErrorBox>{error}</ErrorBox>}
      <Panel
        title="By company: compared with the current template"
        actions={
          <>
            {health && (health.drift ? <Pill tone="warn">{health.drift} compan{health.drift === 1 ? 'y drifts' : 'ies drift'}</Pill> : <Pill tone="ok">all in sync</Pill>)}
            {health?.unreachable ? <Pill tone="bad">{health.unreachable} unreachable</Pill> : null}
            <Btn kind="ghost" small onClick={() => setShowInSync((v) => !v)}>{showInSync ? 'Hide in-sync' : 'Show in-sync'}</Btn>
            <Btn kind="ghost" small onClick={load} disabled={loading}>{loading ? 'Checking…' : 'Re-check'}</Btn>
          </>
        }
        bodyClass="overflow-x-auto"
      >
        {!health && loading && <div className="flex items-center gap-2 p-4 text-sm text-slate-500"><Spinner /> Reading every company's columns…</div>}
        {health && (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Company</th><th className="px-3 py-2">Missing</th><th className="px-3 py-2">Extra</th><th className="px-3 py-2">Which migrations fix this</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 align-top">
              {companies.map((c) => (
                <tr key={c.companyId}>
                  <td className="px-3 py-2">
                    <b className="text-slate-900">{c.companyName}</b>
                    <div className="text-[11px] text-slate-400">{c.databaseName}</div>
                    {c.state === 'unreachable' && <Pill tone="bad">server unreachable</Pill>}
                    {c.state === 'no_db' && <Pill tone="mut">no database</Pill>}
                    {c.state === 'in_sync' && <Pill tone="ok">in sync</Pill>}
                    {c.ignored ? <div className="mt-0.5 text-[11px] text-slate-400">{c.ignored} ignored</div> : null}
                  </td>
                  <td className="px-3 py-2 font-mono text-[12px] text-red-700">
                    {[...(c.missingTables ?? []).map((t) => `− ${t} (table)`), ...(c.missingColumns ?? []).map((x) => `− ${x}`)].slice(0, 12).map((x) => <div key={x}>{x}</div>)}
                    {((c.missingTables?.length ?? 0) + (c.missingColumns?.length ?? 0)) > 12 && <div className="text-slate-500">and more</div>}
                    {c.state === 'drift' && !(c.missingTables?.length || c.missingColumns?.length) && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[12px] text-green-700">
                    {[...(c.extraTables ?? []).map((t) => `+ ${t} (table)`), ...(c.extraColumns ?? []).map((x) => `+ ${x}`)].slice(0, 12).map((x) => <div key={x}>{x}</div>)}
                    {((c.extraTables?.length ?? 0) + (c.extraColumns?.length ?? 0)) > 12 && <div className="text-slate-500">and more</div>}
                  </td>
                  <td className="px-3 py-2">
                    {(c.suggestions ?? []).map((s) => (
                      <button key={s.scriptId} type="button" onClick={() => nav.openPreview(s.scriptId)} className="block text-left text-[12.5px] font-semibold text-blue-600 hover:underline" title={`Mentions ${s.covers.join(', ')}`}>
                        {shortName(s.folderPath)}
                      </button>
                    ))}
                    {c.state === 'drift' && !(c.suggestions ?? []).length && (c.missingTables?.length || c.missingColumns?.length) ? <span className="text-xs text-slate-500">No unapplied migration mentions these.</span> : null}
                    {c.state === 'drift' && (c.missingTables?.length || c.missingColumns?.length) ? (
                      <div className="mt-1.5"><Btn kind="ghost" small onClick={() => setFixFor(c.databaseName)}>Fix drift</Btn></div>
                    ) : null}
                  </td>
                </tr>
              ))}
              {companies.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">Every company matches the template.</td></tr>}
            </tbody>
          </table>
        )}
        {health && <div className="border-t border-slate-200 px-4 py-2 text-xs text-slate-500">Checked {when(health.checkedAt)} · {health.total} databases · template: templates/tenant-schema.sql (the Schema Source on the Templates tab)</div>}
      </Panel>

      <DriftDrawer databaseName={fixFor} onClose={() => setFixFor(null)} nav={nav} isOwner={isOwner} onRan={load} />
    </>
  );
};

/** Fix drift for one company in a side sheet (shared by the company table and Clean up › Template). */
export const DriftDrawer: React.FC<{ databaseName: string | null; onClose: () => void; nav: RunnerNav; isOwner: boolean; onRan?: () => void }> = ({
  databaseName, onClose, nav, isOwner, onRan,
}) => (
  <Drawer open={!!databaseName} onClose={onClose} title={`Fix drift · ${databaseName ?? ''}`} wide>
    {databaseName && (
      <DriftFixSheet
        key={databaseName}
        databaseName={databaseName}
        isOwner={isOwner}
        nav={{ ...nav, openRun: (id) => { onClose(); nav.openRun(id); }, openPreview: (id) => { onClose(); nav.openPreview(id); } }}
        onRan={onRan}
      />
    )}
  </Drawer>
);

/** Lint of the migrations/ folder (loose files, copies, dumps, missing @scope, …). */
export const FolderLintPanel: React.FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const [lint, setLint] = useState<LintResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openRule, setOpenRule] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    runnerApi.lint().then(setLint).catch((err) => setError(errorText(err, 'Could not read migrations/'))).finally(() => setLoading(false));
  }, [refreshKey]);

  const byRule = useMemo(() => {
    const m = new Map<string, LintIssue[]>();
    for (const i of lint?.issues ?? []) {
      if (!m.has(i.rule)) m.set(i.rule, []);
      m.get(i.rule)!.push(i);
    }
    const order = ['data-dump', 'copy-file', 'loose-root-file', 'missing-scope', 'unknown-requires', 'requires-later-folder', 'delimiter', 'non-idempotent', 'same-day-same-table', 'bad-folder-name', 'no-number-prefix', 'non-sql-file'];
    return [...m.entries()].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
  }, [lint]);

  return (
    <Panel
      title="Migration folder"
      actions={lint && <Pill tone={lint.errors ? 'bad' : lint.warnings ? 'warn' : 'ok'}>{lint.errors} to fix · {lint.warnings} warnings · {lint.files} files</Pill>}
    >
      <div className="space-y-2 text-[13px]">
        {error && <ErrorBox>{error}</ErrorBox>}
        {byRule.map(([rule, issues]) => {
          const sev = issues.some((i) => i.severity === 'error') ? 'error' : 'warn';
          return (
            <div key={rule}>
              <button type="button" className="w-full text-left" onClick={() => setOpenRule(openRule === rule ? null : rule)}>
                <Lint tone={sev === 'error' ? 'bad' : 'warn'} label={sev === 'error' ? 'Fix' : 'Warns'}>
                  {(RULE_TEXT[rule] ?? ((n: number) => `${n} × ${rule}`))(issues.length)}{' '}
                  <span className="text-blue-600">{openRule === rule ? 'hide' : 'show'}</span>
                </Lint>
              </button>
              {openRule === rule && (
                <ul className="ml-14 mt-1 max-h-64 space-y-0.5 overflow-auto font-mono text-[11.5px] text-slate-600">
                  {issues.map((i, n) => (
                    <li key={`${i.path}-${n}`}>
                      {i.path}{i.statementIndex !== undefined ? ` (statement ${i.statementIndex + 1})` : ''}
                      {['unknown-requires', 'requires-later-folder', 'same-day-same-table'].includes(i.rule) && <div className="whitespace-normal font-sans text-slate-500">{i.message}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
        {lint && byRule.length === 0 && <Lint tone="ok" label="OK">The migrations folder is clean.</Lint>}
        {!lint && loading && <div className="flex items-center gap-2 text-slate-500"><Spinner /> Reading migrations/…</div>}
      </div>
    </Panel>
  );
};

/** The old Schema health tab (both panels); the Database page now shows them under Clean up. */
const HealthTab: React.FC<{ nav: RunnerNav; isOwner?: boolean }> = ({ nav, isOwner = false }) => (
  <div className="space-y-4 px-6 py-6">
    <CompanyHealthPanel nav={nav} isOwner={isOwner} />
    <FolderLintPanel />
  </div>
);

export default HealthTab;
